import classNames from "classnames"
import Image from "next/image"
import React, { useMemo } from "react"

import { Analytics } from "@bounce/analytics"
import { useLocale, useTranslation } from "@bounce/i18n"
import { getStaticMap } from "@bounce/util"

import { InteractionEvents } from "../../../../libs/analytics/events"
import SafeLink from "../../../Generic/SafeLink/SafeLink"
import { config } from "@/config"
import type { CityData } from "@/libs/bounce/cities"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

type StaticCityMapProps = {
  className?: string
  city: CityData
  nearestStores: NearbyStorageSpot[]
  searchUrl: string
}

const StaticCityMap = ({ city, nearestStores, searchUrl, className }: StaticCityMapProps) => {
  const { t } = useTranslation()
  const { language } = useLocale()

  const staticMapImage = useMemo(() => {
    return getStaticMap({
      width: 1280,
      height: 428,
      latitude: city.coordinates.latitude,
      longitude: city.coordinates.longitude,
      zoom: 12,
      language,
      markers: nearestStores.map(store => ({
        latitude: store.coordinates.latitude,
        longitude: store.coordinates.longitude,
      })),
      mapId: "f7bfb7c4215fe617",
      key: config.googleMaps.key,
    })
  }, [nearestStores, city.coordinates.latitude, city.coordinates.longitude, language])

  return (
    <SafeLink
      href={searchUrl}
      onClick={() => Analytics.track(InteractionEvents.CityMapPressed, { city: city.name })}
      data-chromatic="ignore"
      className={classNames(
        "relative flex h-[218px] w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg md:h-[428px]",
        className,
      )}>
      <Image
        src={staticMapImage}
        width={1311}
        height={428}
        priority
        loading="eager"
        alt={t("cmp.cityMap.mapImgAlt", "Stores in this location on Google Maps.")}
        className="absolute left-0 top-0 size-full scale-[2.5] rounded-lg object-cover object-center sm:scale-100"
      />
    </SafeLink>
  )
}

export default StaticCityMap
